import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { addDays, endOfDay, format, startOfDay } from 'date-fns';
import { Box, FlexBox, FormDialog, Grid, Icon, Typography } from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import {
  KioskSalesByCardType,
  actions as deliveryLogActions,
  selectors as deliveryLogSelectors
} from 'store/deliveryLog';
import { exportKioskSalesReport } from 'utils/sfotKioskSalesReport';
import { useStyles } from '../styled';
import { SFOTSalesReportOverlayProps } from '../types';

export const SFOTSalesReportOverlay = (props: SFOTSalesReportOverlayProps) => {
  const { clientIntegration, onClose, setErrorMessage, sfotSalesReportSettings } = props;

  const classes = useStyles({ isOpen: true })();

  const { getSalesByCardTypeReport, resetSalesByCardTypeReport } = useActions({
    getSalesByCardTypeReport: deliveryLogActions.getSalesByCardTypeReport,
    resetSalesByCardTypeReport: deliveryLogActions.resetSalesByCardTypeReport
  });

  const salesByCardTypeReport = useSelector(deliveryLogSelectors.selectSalesByCardTypeReport);

  const [date, setDate] = useState<Date | null>(new Date());

  const [salesReportStatus, setSalesReportStatus] = useState('');
  const [salesReportDayOfEvent, setSalesReportDayOfEvent] = useState(0);
  const [salesReportCurrentYearDate, setSalesReportCurrentYearDate] = useState('');
  const [salesReportPreviousYearDate, setSalesReportPreviousYearDate] = useState('');
  const [oneDayCurrentYearSalesByKiosk, setOneDayCurrentYearSalesByKiosk] = useState<
    KioskSalesByCardType[] | undefined
  >();
  const [currentYearSalesByKiosk, setCurrentYearSalesByKiosk] = useState<KioskSalesByCardType[] | undefined>();
  const [oneDayPreviousYearSalesByKiosk, setOneDayPreviousYearSalesByKiosk] = useState<
    KioskSalesByCardType[] | undefined
  >();
  const [previousYearSalesByKiosk, setPreviousYearSalesByKiosk] = useState<KioskSalesByCardType[] | undefined>();

  const isConfirmDisabled = !date;

  /**
   * This triggers actions to build the kiosk sales report for SFT.
   *
   * setTimeout() is used to ensure that the state updates are
   * completed before the next action is triggered.
   */
  useEffect(() => {
    if (salesByCardTypeReport.data && salesReportStatus === 'oneDayCurrentYear') {
      setOneDayCurrentYearSalesByKiosk(salesByCardTypeReport.data);
      setSalesReportStatus('oneDayPreviousYear');
      resetSalesByCardTypeReport();

      setTimeout(() => {
        getSalesByCardTypeReport({
          clientIntegrationId: clientIntegration.id,
          startTime: convertDateStrToISOString(salesReportPreviousYearDate, { useStartOfDay: true }),
          endTime: convertDateStrToISOString(salesReportPreviousYearDate, { useEndOfDay: true })
        });
      }, 0);
    } else if (salesByCardTypeReport.data && salesReportStatus === 'oneDayPreviousYear') {
      setOneDayPreviousYearSalesByKiosk(salesByCardTypeReport.data);
      setSalesReportStatus('currentYear');
      resetSalesByCardTypeReport();

      setTimeout(() => {
        getSalesByCardTypeReport({
          clientIntegrationId: clientIntegration.id,
          startTime: convertDateStrToISOString(sfotSalesReportSettings.currentEventStartDate!, { useStartOfDay: true }),
          endTime: convertDateStrToISOString(salesReportCurrentYearDate, { useEndOfDay: true })
        });
      }, 0);
    } else if (salesByCardTypeReport.data && salesReportStatus === 'currentYear') {
      setCurrentYearSalesByKiosk(salesByCardTypeReport.data);
      setSalesReportStatus('previousYear');
      resetSalesByCardTypeReport();

      setTimeout(() => {
        getSalesByCardTypeReport({
          clientIntegrationId: clientIntegration.id,
          startTime: convertDateStrToISOString(sfotSalesReportSettings.previousEventStartDate!, {
            useStartOfDay: true
          }),
          endTime: convertDateStrToISOString(salesReportPreviousYearDate, { useEndOfDay: true })
        });
      }, 0);
    } else if (salesByCardTypeReport.data && salesReportStatus === 'previousYear') {
      setPreviousYearSalesByKiosk(salesByCardTypeReport.data);
      setSalesReportStatus('exportData');
      setTimeout(() => resetSalesByCardTypeReport(), 0);
    } else if (salesReportStatus === 'exportData') {
      setSalesReportStatus('');

      exportKioskSalesReport({
        sfotSalesReportSettings,
        salesReportCurrentYearDate,
        salesReportPreviousYearDate,
        salesReportDayOfEvent,
        oneDayCurrentYearSalesByKiosk,
        oneDayPreviousYearSalesByKiosk,
        currentYearSalesByKiosk,
        previousYearSalesByKiosk
      });

      resetSalesByCardTypeReport();
      onClose();
    }
  }, [salesByCardTypeReport.data]);

  // ON FETCH SALES REPORT ERROR
  useEffect(() => {
    if (salesByCardTypeReport.error) {
      setErrorMessage('Uh oh! Something went wrong. Please try again.');
      resetSalesByCardTypeReport();
    }
  }, [salesByCardTypeReport.error]);

  /**
   * Convert a date string (in format 'yyyy-MM-dd') to an ISO string
   * with time set to start of day or end of day based on options.
   */
  const convertDateStrToISOString = (dateStr: string, options: { useStartOfDay?: boolean; useEndOfDay?: boolean }) => {
    let result = new Date(`${dateStr}T00:00:00`);
    if (options.useStartOfDay) result = startOfDay(result);
    if (options.useEndOfDay) result = endOfDay(result);

    return result.toISOString();
  };

  const onConfirm = () => {
    if (!date) {
      setErrorMessage('Please select a date.');
      return;
    }

    if (!sfotSalesReportSettings?.currentEventStartDate || !sfotSalesReportSettings?.previousEventStartDate) {
      setErrorMessage('Sales report settings are not configured properly.');
      return;
    }

    const selectedDateISO = date.toISOString();
    const selectedDateObj = new Date(selectedDateISO);
    const currentYearReportDate = format(selectedDateObj, 'yyyy-MM-dd');
    setSalesReportCurrentYearDate(currentYearReportDate);
    const date1 = new Date(`${sfotSalesReportSettings.currentEventStartDate}T00:00:00`);
    const date2 = new Date(`${currentYearReportDate}T00:00:00`);
    const diffInDays = Math.round((date2.getTime() - date1.getTime()) / (1000 * 3600 * 24));
    const previousYearSameDay = addDays(
      new Date(`${sfotSalesReportSettings.previousEventStartDate}T00:00:00`),
      diffInDays
    );
    const prevDate = format(previousYearSameDay, 'yyyy-MM-dd');

    setSalesReportPreviousYearDate(prevDate);
    setSalesReportDayOfEvent(diffInDays + 1);
    setSalesReportStatus('oneDayCurrentYear');

    getSalesByCardTypeReport({
      clientIntegrationId: clientIntegration.id,
      startTime: convertDateStrToISOString(currentYearReportDate, { useStartOfDay: true }),
      endTime: convertDateStrToISOString(currentYearReportDate, { useEndOfDay: true })
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const DatePickerCustomInput = React.forwardRef((props1: any, ref: any) => {
    return (
      <div
        onClick={props1.onClick}
        ref={ref}
        className={classes.datePickerInput}
        style={{ padding: props1.value ? '0 40px 0 12px' : '0 12px' }}
      >
        <label>{props1.value || props1.placeholder}</label>
        <Icon name="CalendarToday" sx={{ color: '#F46D2E' }} />
      </div>
    );
  });

  return (
    <FormDialog
      cancelButtonAction={onClose}
      cancelButtonText="Cancel"
      confirmButtonAction={onConfirm}
      confirmButtonDisabled={isConfirmDisabled}
      confirmButtonIsLoading={salesByCardTypeReport.pending || salesReportStatus !== ''}
      confirmButtonText="Build Report"
      open
      title="Sales Report"
    >
      <Grid direction="column" width="100%" marginTop="10px">
        <FlexBox className={classes.filtersFieldsFlexBox}>
          <Box className={classes.filtersFieldBox}>
            <Typography className={classes.filtersFieldLabel}>Select Date</Typography>
            <div className="my-datepicker-wrapper">
              <div className={classes.datePickerContainer}>
                <DatePicker
                  selected={date}
                  onChange={(d) => setDate(d)}
                  placeholderText="MM/DD/YYYY"
                  dateFormat="MM/dd/yyyy"
                  className={classes.datePickerComponent}
                  isClearable
                  customInput={<DatePickerCustomInput />}
                  popperPlacement="bottom-start"
                  popperProps={{ strategy: 'fixed' }}
                />
              </div>
            </div>
          </Box>
        </FlexBox>
      </Grid>
    </FormDialog>
  );
};
